/*
** $Id$
**
** Copyright 2018 Port25 Solutions, Inc.
** All rights reserved.
*/


import React, { Component } from 'react';
import { Container, Row, Col, FormGroup, FormLabel, Form, ListGroup, ListGroupItem,
         Button } from 'react-bootstrap';
import { StickyContainer, Sticky } from 'react-sticky';
import Autosuggest from 'react-autosuggest';
import CommandHelp from './CommandHelp'
import { updateComponent, getFromJson } from '../util/Utils'
import { getCommandName, getCommandHelp } from '../util/CommandFormatter'
import { pmta } from '../util/HttpsClient'
import '../css/command.css';
import PropTypes from 'prop-types';


/**
 * Command page of web monitor.
 */
export default class Command extends Component {
    constructor(props) {
        super(props)
        this.update = this.update.bind(this)
        this.hideHelp = this.hideHelp.bind(this)
        this.format = this.format.bind(this)
        this.fillClickedCommand = this.fillClickedCommand.bind(this)
        this.makeAutosuggest = this.makeAutosuggest.bind(this)
        this.getSuggestions = this.getSuggestions.bind(this)
        this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this)
        this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this)
        this.onSuggestionHighlighted = this.onSuggestionHighlighted.bind(this)
        this.onSuggestionSelected = this.onSuggestionSelected.bind(this)
        this.getSuggestionValue = this.getSuggestionValue.bind(this)
        this.renderSuggestion = this.renderSuggestion.bind(this)
        this.onAutoComplete = this.onAutoComplete.bind(this)
        this.getCommands = this.getCommands.bind(this)
        this.onKeyDown = this.onKeyDown.bind(this)
        this.storeAutosuggestRef = this.storeAutosuggestRef.bind(this)
        this.state = {
            command: '',
            syntax: '',
            suggestions: []
        }
    }


    static propTypes = {
        /**
         * What to call to update errors.
         */
        showError: PropTypes.func.isRequired,

        /**
         * Sets how to refresh this component upon auto refresh.  Since this
         * component does not auto refresh itself, this is solely used for
         * purposes of disabling the auto refresh feature.
         */
        setRefresh: PropTypes.func.isRequired
    };


    async componentDidMount() {
        this.props.setRefresh(undefined)
        await updateComponent(this, '/command', {
            format: 'json-text'
        }, () => {
            this.setState({
                availableCommands: this.getCommands()
            })
        })
    }


    /**
     * Runs the command specified in command input and updates this component
     * with the output.
     * 
     * @param event
     *      the event triggered by submitting the form.
     */
    async update(event) {
        event.preventDefault()
        await this.runCommand()
    }


    /**
     * Runs the command specified in the command input.
     */
    async runCommand() {
        let response = {}
        let error = ''
        let csrfToken
        let syntax = {
            syntax: ''
        }
        try {
            csrfToken = getFromJson(this.state, ['data', 'CSRFToken'])
            const trimmedCommand = this.state.command ? this.state.command.trim() : ''
            response = await pmta.post('/command', {
                command: trimmedCommand,
                CSRFToken: csrfToken,
                format: 'json-text'
            })
            response.command = '';
            this.setState(Object.assign(response, syntax))
        }
        catch (ex) {
            error = ex.toString()
            this.setState(Object.assign({
                data: {
                    data: undefined,
                    CSRFToken: csrfToken
                }
            }, syntax))
        }
        this.props.showError(error)
    }


    format(text) {
        if (text && text.trim().indexOf('Available commands') > -1) {
            const commands = this.getCommands(text).map((line) => {
                return (
                    <ListGroupItem key={line} data-command={line}
                                   onClick={this.fillClickedCommand}>
                        {line}
                    </ListGroupItem>
                )
            })
            return (
                <div>
                    <div className='availableCommands'>Available commands</div>
                        <ListGroup>
                            {commands}
                        </ListGroup>
                    </div>
            )
        }
        else {
            return text && <pre className='output'>{text}</pre>
        }
    }


    fillClickedCommand(event) {
        this.setState({
            // suffix a space, for better usability
            command: getCommandName(event.currentTarget.dataset.command) + ' ',
            syntax: event.currentTarget.dataset.command
        })
        if (this.commandInput) {
            this.commandInput.focus()
        }
        this.onSuggestionsClearRequested()
    }


    onAutoComplete(event, { newValue }) {
        const allCommands = this.state.availableCommands || []
        const filtered = allCommands.filter(c => 
            c.toLowerCase().indexOf(newValue.toLowerCase()) === 0
        )
        let syntax = {}
        if (!this.state.syntax || filtered.length === 1) {
            syntax = {
                syntax: filtered.length === 1 ? filtered[0] : ''
            }
        }
        this.setState(Object.assign({
            command: newValue
        }, syntax))
    }


    onSuggestionsFetchRequested = ({ value }) => {
        this.setState({
            suggestions: this.getSuggestions(value)
        });
    };


    onSuggestionsClearRequested = () => {
        this.setState({
            suggestions: []
        });
    };


    onSuggestionHighlighted = ({ suggestion }) => {
        this.setState({
            highlightedSuggestion: suggestion 
        });
    };


    onSuggestionSelected = (event, { suggestionValue }) => {
        // suffix a space, for better usability
        this.setState({
            command: suggestionValue + ' ' 
        })
    };


    getCommands(text) {
        const output = text || getFromJson(this.state, ['data', 'data'], '')
        if (output && output.trim().indexOf('Available commands') > -1) {
            let lines = output.split('\n')
            lines.shift()
            return lines.filter(line => line && line.trim())
        }
        return []
    }


    getSuggestions(value) {
        if (!value) {
            return []
        }

        const inputValue = value.toLowerCase();
        const inputLength = inputValue.length;
        let commands = this.state.availableCommands || this.getCommands()
        commands = commands.map(c => getCommandName(c))
        return (inputLength === 0
            ? []
            : commands.filter(c => c.toLowerCase().slice(0, inputLength) === inputValue)
        )
    }


    // needed by react-autosuggest
    getSuggestionValue(suggestion) {
        return suggestion
    }


    renderSuggestion(suggestion) {
        return (<div>{suggestion}</div>)
    }


    async onKeyDown(event) {
        if (event.key === 'Enter') {
            event.preventDefault()
            if (!this.state.highlightedSuggestion) {
                this.update(event)
            }
        }
    }


    storeAutosuggestRef(autosuggest) {
        if (autosuggest !== null) {
            this.commandInput = autosuggest.input;
        }
    }


    makeAutosuggest() {
        const { command, suggestions } = this.state;
        const inputProps = {
            placeholder: 'help',
            value: command,
            onChange: this.onAutoComplete,
            onKeyDown: this.onKeyDown
        };

        return <Autosuggest
                   suggestions={suggestions}
                   onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                   onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                   onSuggestionHighlighted={this.onSuggestionHighlighted}
                   onSuggestionSelected={this.onSuggestionSelected}
                   getSuggestionValue={this.getSuggestionValue}
                   renderSuggestion={this.renderSuggestion}
                   inputProps={inputProps}
                   ref={this.storeAutosuggestRef}/>
    }


    hideHelp() {
        this.setState({
            syntax: ''
        })
    }


    render() {
        let output = this.format(getFromJson(this.state, ['data', 'data'], ''))
        const help = getCommandHelp(this.state.syntax)
        return (
            <StickyContainer>
                <Form>
                    <Container>
                        <Row>
                            <Col md={12} className='commandInput'>
                                <FormGroup>
                                    <FormLabel className='commandHeader'>
                                        Command
                                    </FormLabel>
                                    <Sticky>
                                        {
                                            ({ style }) =>
                                                <div style={style}>
                                                    {this.makeAutosuggest()}
                                                </div>
                                        }
                                    </Sticky>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            {help && <CommandHelp command={help}/>}
                        </Row>
                        <Row>
                            {help && <div className='hideHelp'>
                                        <Button variant='primary' onClick={this.hideHelp}>
                                            Hide Command Syntax
                                        </Button>
                                     </div>
                            }
                        </Row>
                        <Row>
                            {
                               output 
                            }
                        </Row>
                    </Container>
                </Form>
            </StickyContainer>
        );
    }
}
